import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import type { InlineMessageVariant } from '@ingka/inline-message-webc';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';

import OpenDesignCodeStateEnum from '../../enums/OpenDesignCodeStateEnum.js';
import type IOpenDesignCodeLocalizedInformation from '../../types/IOpenDesignCodeLocalizedInformation.js';
import type IOpenDesignCodeTranslations from '../../types/IOpenDesignCodeTranslations.js';
import OpenDesignCodeInput from '../open-design-code-input/OpenDesignCodeInput.js';
import OpenDesignCodeStyle from './OpenDesignCodeStyle.js';
/**
 * @event open When button "open" is clicked.
 * @event input Emitted when user writes a character in the input field.
 */

export default class OpenDesignCode extends KompisElement {
	// Theme object
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: IOpenDesignCodeLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IOpenDesignCodeTranslations | null = null;

	// The state. Valid state are in OpenDesignCodeState
	@property({ type: String })
	public state: OpenDesignCodeStateEnum = OpenDesignCodeStateEnum.default;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const primaryVariant: ButtonVariant = 'primary';

		return html`
			<style>
				${OpenDesignCodeStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="container">
				<kompis-open-design-code-input
					@open="${this.onOpen.bind(this)}"
					@input="${this.onInput.bind(this)}"
					class="input"
					.theme="${this.theme}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.state="${this.state}"
				></kompis-open-design-code-input>

				<div class="divider"></div>

				<skapa-button
					class="button"
					?disabled="${this.isButtonDisabled()}"
					variant="${primaryVariant}"
					size="small"
					@click="${this.onButtonClick.bind(this)}"
					?loading="${this.state === OpenDesignCodeStateEnum.loading}"
					loadingSrText="${this.getTranslations().loading}"
				>
					${this.getTranslations().sprGalleryOpen}
				</skapa-button>
			</div>

			${this.getInlineMessage()}
		`;
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(warningTriangle);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('kompis-open-design-code-input', OpenDesignCodeInput);

		super.connectedCallback();
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IOpenDesignCodeTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <IOpenDesignCodeTranslations>translations;
	}

	private getInlineMessage(): RenderResult {
		return html`<div aria-live="assertive">${this.getUnavailableDesignErrorMessage()}</div>`;
	}

	private getUnavailableDesignErrorMessage(): RenderResult {
		if (this.state !== OpenDesignCodeStateEnum.failed) {
			return null;
		}

		const error = this.getTranslations().invalidConfigurationMessage;
		const cautionaryVariant: InlineMessageVariant = 'cautionary';

		return html`
			<skapa-inline-message subtle variant="${cautionaryVariant}">
				<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
				<span slot="body">${error}</span>
			</skapa-inline-message>
		`;
	}

	private isButtonDisabled(): boolean {
		if (this.state === OpenDesignCodeStateEnum.loading) {
			return false;
		}

		return !this.getOpenDesignCodeInput()?.isValidVpcCode();
	}

	private onOpen(): void {
		if (!this.isButtonDisabled()) {
			this.onButtonClick();
		}
	}

	private onInput({ detail }: CustomEvent): void {
		this.requestUpdate();
		this.dispatchEvent(
			new CustomEvent('input', {
				bubbles: true,
				detail
			})
		);
	}

	private onButtonClick(): void {
		this.dispatchEvent(
			new CustomEvent('open', {
				bubbles: true,
				detail: {
					vpcCode: this.getOpenDesignCodeInput()?.getVpcCode() ?? ''
				}
			})
		);
	}

	private getOpenDesignCodeInput(): OpenDesignCodeInput | null {
		return (
			this.shadowRoot.querySelector(
				this.customElements.getInternalName('kompis-open-design-code-input')
			) ?? null
		);
	}
}
declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'skapa-inline-message': SkapaInlineMessage;
		'kompis-open-design-code': OpenDesignCode;
	}
}
